import sortBy from 'lodash/sortBy';
import { fakeProviders } from '@/integrations/data';
import { getErrorMessage } from '@/store/alerts/mutations.js';

// Featured integrations will honor the below order.
const isFeatured = ['constant-contact', 'sendinblue-v2', 'activecampaign'];

export default {
	fetchIntegrationData({ commit, rootGetters, rootState }) {
		commit('loading', 'integrations', { root: true });

		let allProviders = [];
		let integrations = [];
		let featuredProviders = [];
		let legacyProviders = [];
		let searchResults = [];

		const firstFetch = !rootGetters.isFetched('integrationProviders');

		return new Promise((resolve) => {
			commit('fetching', 'integrationProviders', { root: true });

			this._vm.$api.get('providers').then(({ body }) => {
				allProviders = body;
				allProviders.forEach((p) => {
					// If legacy, add to array.
					if (p.legacy) {
						legacyProviders.push(p.slug);
					}

					// Setup integrations & featured key.
					p.integrations = [];
					p.featured = false;

					p.logo = p.logo.replace(
						'app.optinmonster.com', // Replace production domain
						'app.omwpapi.com' // With our omwpapi domain, to get around adblockers.
					);
				});

				featuredProviders = isFeatured.map((slug) => {
					const provider = allProviders.find((p) => p.slug === slug);
					let clone = {};

					if (provider) {
						clone = Object.assign({}, provider);
						clone.featured = true;
						clone.slug = `${provider.slug}-featured`;
					}

					return clone;
				});

				// Parse the providers.
				const parseProviders = () => {
					allProviders = allProviders
						// Remove MonserLeads.
						.filter((r) => 'monsterleads' !== r.slug)
						// Remove legacy providers.
						.filter((r) => -1 === legacyProviders.indexOf(r.slug));

					// Add any "fake" providers.
					if (fakeProviders.length) {
						fakeProviders.forEach((f) => {
							allProviders.push({
								name: f.name,
								slug: f.slug,
								integrations: [],
								isFake: true,
								canBypass: false,
								supports: {
									categories: f.categories,
								},
							});
						});
					}

					// Alphabetize the providers.
					integrations = sortBy(integrations, ['slug']);
					allProviders = sortBy(allProviders, ['slug']);

					allProviders.push({
						name: "Don't See Yours?",
						slug: 'fallbackprovider',
						integrations: [],
						isFake: true,
						canBypass: false,
						supports: {
							categories: ['email-marketing', 'crm', 'remarketing'],
						},
					});
				};

				// Prime the search results.
				const primeSearch = () => {
					allProviders.forEach((p) => {
						searchResults.push(p.slug);
					});
				};

				// Save providers to state and finish.
				const saveAndFinish = () => {
					parseProviders();
					primeSearch();

					// Add custom OM Analytics provider.
					integrations.push({
						name: 'OM Analytics',
						slug: 'omanalytics',
						integrations: [],
						canBypass: true,
					});

					// Add custom Google Analytics provider.
					integrations.unshift({
						name: 'Google Analytics',
						slug: 'googleanalytics',
						integrations: [],
						canBypass: true,
					});

					commit('fetched', 'integrations', { root: true });

					commit('setSearchResults', searchResults);
					commit('integrations', integrations);
					commit('providers', allProviders);
					commit('featuredProviders', featuredProviders);
					commit('doneLoading', 'integrations', { root: true });
					resolve();
				};

				commit('fetched', 'integrationProviders', { root: true });

				// If there's not an API key, parse what we have and return
				// before making the call for the user's integrations.
				if (!rootState.apiKey) {
					return saveAndFinish();
				}

				commit('fetching', 'integrations', { root: true });

				this._vm.$api
					.get({
						endpoint: 'integrations',
						cached: false,
					})
					.then(({ body }) => {
						body.forEach((i) => {
							// Find the provider
							const provider = allProviders.find((p) => p.slug === i.provider);

							// If no provider found, bail.
							if (!provider) {
								return;
							}

							// We need to clone the provider, so we can have an existing provider (My Integrations)
							// and a provider in all providers. This allows them to keep the correct card "state".
							const clone = Object.assign({}, provider);
							clone.existing = true;
							clone.slug = `${provider.slug}-existing`;

							// Check if provider exists
							const existing = integrations.find((ui) => ui.slug === `${i.provider}-existing`);

							if (existing) {
								existing.integrations.push(i);
							} else {
								clone.integrations.push(i);
								integrations.push(clone);

								// Remove provider from featured integrations.
								featuredProviders = featuredProviders.filter((r) => {
									return r.slug.replace(/-(featured|existing)/, '') !== clone.slug;
								});
							}
						});

						// Reset providers to allow for "connectable" state.
						allProviders.forEach((p) => {
							if (p.integrations.length) {
								p.integrations = [];
							}
						});

						if (firstFetch) {
							saveAndFinish();
						} else {
							setTimeout(() => saveAndFinish(), 2000);
						}
					});
			});
		});
	},
	deleteIntegration({ commit, dispatch }, { id }) {
		commit('loading', 'deleteIntegration', { root: true });
		return new Promise((resolve, reject) => {
			commit('setIsDeleting', true);
			this._vm.$api
				.delete(`integrations/${id}`)
				.then(() => {
					commit('setIsDeleting', false);
					dispatch('resetIntegrations', {
						message: 'Integration successfully deleted. Refreshing...',
						type: 'info',
						closes: true,
					});
					resolve();
				})
				.catch((error) => {
					commit('setIsDeleting', false);
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error, integrationId: id });
				})
				.finally(() => {
					commit('doneLoading', 'deleteIntegration', { root: true });
				});
		});
	},
	updateIntegration({ commit, dispatch }, { id, payload }) {
		commit('loading', 'updateIntegration', { root: true });

		return new Promise((resolve, reject) => {
			let endpoint = `integrations/${id}`;
			const isPardot = 'salesforce-pardot' === payload.providerSlug;

			if (isPardot) {
				endpoint += '?include=providerData';
			}
			this._vm.$api
				.put(endpoint)
				.send(payload)
				.then(({ body }) => {
					if (!isPardot) {
						dispatch('resetIntegrations', {
							message: 'Integration updated! Refreshing...',
							type: 'info',
							closes: true,
						});
					}
					resolve(body);
				})
				.catch((error) => {
					const message = error.response.body.message || 'Integration could not be updated';
					commit('setActiveContentError', message);
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error, integrationId: id });
				})
				.finally(() => {
					commit('doneLoading', 'updateIntegration', { root: true });
				});
		});
	},
	addIntegration({ commit, dispatch }, { payload }) {
		// Adding has different keys than editing,
		// so map them over. ¯\_(ツ)_/¯
		if (payload.name) {
			payload.label = payload.name;
			delete payload.name;
		}
		if (payload.providerSlug) {
			payload.provider = payload.providerSlug;
			delete payload.providerSlug;
		}
		if (payload.providerData) {
			payload.provider_data = payload.providerData;
			delete payload.providerData;
		}

		commit('loading', 'addIntegration', { root: true });

		const isPardot = 'salesforce-pardot' === payload.provider;

		return new Promise((resolve, reject) => {
			this._vm.$api
				.post('integrations')
				.send(payload)
				.then(({ body }) => {
					if (!isPardot) {
						dispatch('resetIntegrations', {
							message: 'Integration added! Refreshing...',
							type: 'info',
							closes: true,
						});
					}
					resolve(body);
				})
				.catch((error) => {
					error.explanation = 'Integration could not be created.';
					const msg = getErrorMessage(error).replace(/pre>/gi, 'blockquote>');
					commit('setActiveContentError', msg);
					reject(error);
					// eslint-disable-next-line no-console
					console.error(error);
				})
				.finally(() => {
					commit('doneLoading', 'addIntegration', { root: true });
				});
		});
	},
	oauthPost({ commit }, payload) {
		this._vm.$bus.$emit('oauthPost', payload);

		return new Promise((resolve, reject) => {
			this._vm.$api
				.post('integrations/oauth')
				.send(payload)
				.then(({ body }) => {
					this._vm.$bus.$emit('oauthComplete', body);
					commit('setActiveContentError', '');
					commit('setOauthActive', 'complete');

					resolve(body);
				})
				.catch((error) => {
					error.explanation = 'Integration could not be updated.';
					const msg = getErrorMessage(error).replace(/pre>/gi, 'blockquote>');
					commit('setActiveContentError', msg);
					reject(error);
					// eslint-disable-next-line no-console
					console.error(error);
				});
		});
	},
	configureProvider({ commit, dispatch, getters }, slug) {
		dispatch('resetProviders');
		const integration = getters.getIntegration(slug.replace('-featured', '-existing'));
		if (!integration) {
			return;
		}

		commit('setActiveCard', slug);
		commit('setActiveContentTitle', `Existing ${integration.name} Integrations`);
		if (0 === slug.indexOf('monsterleads')) {
			dispatch('editIntegration', integration.integrations[integration.integrations.length - 1]);
		}

		this._vm.$bus.$once('integrationOpened', () => {
			this._vm.$bus.$emit('scrollToRef', `card-${slug}`, {
				offset: -40, // The admin bar, and some additional padding.
			});
		});
	},
	connectProvider({ commit, dispatch, getters }, slug) {
		dispatch('resetProviders');
		const provider = getters.getProvider(slug.replace('-featured', '').replace('-existing', ''));

		commit('setActiveCard', slug);
		commit('setAddingIntegration', true);
		commit('setActiveContentTitle', `Add a New ${provider.name} Integration`);

		this._vm.$bus.$once('integrationOpened', () => {
			this._vm.$bus.$emit('scrollToRef', `card-${slug}`, {
				offset: -40, // The admin bar, and some additional padding.
			});
		});
		this._vm.$bus.$emit('transitionSlideLayout');
	},
	editIntegration({ commit }, userIntegration) {
		commit('setEditingIntegration', true);
		commit('setActiveIntegration', userIntegration);
		this._vm.$bus.$emit('transitionSlideLayout');
	},
	resetIntegrations({ state, commit, dispatch }, alert) {
		const slug = state.activeCard.replace(/-(featured|existing)/, '') + '-existing';

		commit('loading', 'resetIntegrations', { root: true });

		commit('setOauthActive', false);
		commit('setActiveContentError', '');
		commit('setActiveContentMessage', '');

		if (alert) {
			commit('closeAlerts', null, { root: true });
			commit('maybeAddAlert', alert, { root: true });
		}

		commit('fetching', 'integrations', { root: true });
		setTimeout(() => {
			dispatch('resetProviders');
			dispatch('fetchIntegrationData').then(() => {
				setTimeout(() => {
					dispatch('configureProvider', slug);
					if (alert) {
						commit('maybeRemoveAlert', alert, { root: true });
					}
					commit('doneLoading', 'resetIntegrations', { root: true });
				}, 400);
			});
		}, 100);
	},
	resetProviders({ commit }) {
		commit('setActiveCard', '');
		commit('setActiveContentTitle', '');
		commit('setAddingIntegration', false);
		commit('setEditingIntegration', false);
		commit('setOauthActive', false);
		commit('setActiveContentError', '');
		commit('setActiveContentMessage', '');
	},
};
